import React from "react";
import { summarizeOverview, formatWhen, TRANSMITTED_EXPLANATION, DOC_TYPE_LABELS, jobStateSpec } from "../../lib/printing/printingModel";
import { SectionCard, Notice, Pill, StateBadge, EmptyState, btnLink } from "./PrintingUi";
import AutomaticReceiptsPanel from "./AutomaticReceiptsPanel";
import AutomaticPackingSlipsPanel from "./AutomaticPackingSlipsPanel";

function Stat({ label, value, tone = "neutral", testId }) {
  return (
    <div className="rounded-md border border-stone-200 bg-stone-50 p-3" data-testid={testId}>
      <div className="text-xs uppercase tracking-wide text-stone-600">{label}</div>
      <div className="text-2xl font-semibold text-stone-900">{value}</div>
      {tone !== "neutral" && <span className="sr-only">{tone}</span>}
    </div>
  );
}

export default function PrintingOverview({ agents, printers, jobs, documents, automaticPrinting, isAdmin, onChanged, notify, onGoTo }) {
  const s = summarizeOverview({ agents, printers, jobs, effective: documents?.effective || null });
  return (
    <div className="space-y-4" data-testid="printing-overview">
      <AutomaticReceiptsPanel status={automaticPrinting} isAdmin={isAdmin} onChanged={onChanged} notify={notify} />
      <AutomaticPackingSlipsPanel status={automaticPrinting} isAdmin={isAdmin} onChanged={onChanged} notify={notify} onGoTo={onGoTo} />
      <SectionCard title="Status" description="Live view of your printing platform. Refreshes automatically.">
        <div className="flex flex-wrap items-center gap-2">
          <Pill tone={s.printingEnabled ? "success" : "warning"} testId="overview-printing-enabled">
            {s.printingEnabled ? "Printing enabled" : "Printing disabled"}
          </Pill>
          {s.enabledDocs.map((d) => <Pill key={d} tone="neutral">{DOC_TYPE_LABELS[d] || d}</Pill>)}
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat label="Registered agents" value={s.agents.total} testId="stat-agents" />
          <Stat label="Agents online / offline" value={`${s.agents.online} / ${s.agents.offline}`} testId="stat-agents-online" />
          <Stat label="Configured printers" value={s.printers.total} testId="stat-printers" />
          <Stat label="Reachable / unreachable / unknown" value={`${s.printers.reachable} / ${s.printers.unreachable} / ${s.printers.unknown}`} testId="stat-printers-reach" />
          <Stat label="Jobs queued" value={s.jobs.queued} testId="stat-queued" />
          <Stat label="Jobs transmitting" value={s.jobs.transmitting} testId="stat-transmitting" />
          <Stat label="Jobs transmitted" value={s.jobs.transmitted} testId="stat-transmitted" />
          <Stat label="Retry pending" value={s.jobs.retryable} testId="stat-retryable" />
          <Stat label="Failed" value={s.jobs.terminal} testId="stat-terminal" />
          <Stat label="Needs attention (unknown outcome)" value={s.jobs.ambiguous} tone={s.jobs.ambiguous ? "danger" : "neutral"} testId="stat-ambiguous" />
          <Stat label="Last transmission" value={s.lastTransmission ? formatWhen(s.lastTransmission) : "none yet"} testId="stat-last-transmission" />
        </div>
        <p className="text-xs text-stone-600" data-testid="transmitted-explanation">{TRANSMITTED_EXPLANATION}</p>
      </SectionCard>

      <SectionCard title="Needs your attention" description="Configuration warnings and recent problems.">
        {s.warnings.length === 0 && s.recentErrors.length === 0 && <EmptyState testId="overview-all-clear">No warnings. Everything is configured and healthy as far as the platform can tell.</EmptyState>}
        {s.warnings.map((w) => (
          <Notice key={w.docType ? `${w.code}-${w.docType}` : w.code} tone={w.code === "ambiguous" ? "danger" : "warning"} testId={w.docType ? `warning-${w.code}-${w.docType}` : `warning-${w.code}`}>
            {w.text}{" "}
            {w.code === "no_agent" && <button type="button" className={btnLink} onClick={() => onGoTo("agents")}>Go to Print Agents</button>}
            {(w.code === "no_printer" || w.code === "no_printer_for_role") && <button type="button" className={btnLink} onClick={() => onGoTo("printers")}>Go to Printers</button>}
            {w.code === "no_printer_for_role" && <> · <button type="button" className={btnLink} onClick={() => onGoTo("defaults")}>Go to Document defaults</button></>}
            {w.code === "ambiguous" && <button type="button" className={btnLink} onClick={() => onGoTo("jobs", { ambiguousOnly: true })}>Review jobs</button>}
          </Notice>
        ))}
        {s.recentErrors.length > 0 && (
          <ul className="divide-y divide-stone-200 rounded-md border border-stone-200" data-testid="overview-recent-errors">
            {s.recentErrors.map((e) => (
              <li key={e.id} className="flex flex-col gap-1 p-2 text-sm sm:flex-row sm:items-center sm:gap-3">
                <StateBadge state={e.state} />
                <span className="font-mono text-xs text-stone-600">{e.id.slice(0, 8)}…</span>
                <span className="text-stone-800">{e.code || jobStateSpec(e.state).label}</span>
                <span className="text-xs text-stone-600 sm:ml-auto">{formatWhen(e.at)}</span>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}
